import { containers } from '@ecosystem/newron-design-system';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FilterCarryOverScope } from '@utils/FilterSettings';
import {
  checkFourFilterTypesSelected,
  getDisplayValue,
  isMulti,
  isMultiPgCommonFeatureAllowed,
  MaxAllowedFilters,
  updateSelectedFilters,
} from '@utils/filterTrayUtils/FilterTrayUtils';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { FilterKeys, FilterObject, Filters, Option, PageName, PageType, SubGroupFilter } from '@utils/pageConfig/Types';
import { NO_OPTIONS } from '@utils/StringConstants';
import { isCurrentFilterProductFeature, isSelectedDiffWIthAppliedFilters } from '@utils/validator/CommonValidator';
import FilterSelector from './FilterSelector';
import { actionCreatorsSideFilters } from '@state/Index';
import { IRootState } from '@state/reducers/Reducers';
import { InitialFilter } from '@state/reducers/initial-filter/InitialFiltersReducer';
import { byFilterKey } from '@utils/pageConfig/Predicates/SelectedFiltersPredicates';
import {
  canShowExclusionToggle,
  getChartFilters,
  isEmptyOrNoOptions,
  isExclusionActiveForFilter,
} from '@utils/FilterUtils';
import { useFilters } from '@pages/explore/UseFilters';
import { getExchangeRate } from '@utils/currencyUtils/CurrencyUtils';
import { formatPriceBandAsPerExchangeRate } from '@utils/Formatters';
import { ExclusionProps } from '@components/design/WithMultiSelectToggle';

/* eslint-disable-next-line */
export interface SubFilterProps {
  filterObject: FilterObject;
  isVirtualList?: boolean;
}

const FilterComponent: React.FC<SubFilterProps> = ({ filterObject, isVirtualList = true }) => {
  const pagesConfig = usePageConfig();
  const isSideFiltersFeatureFlagEnabled = pagesConfig.isSideFiltersFeatureFlagEnabled;
  const currentPage = pagesConfig.pageType;
  const currentPageName = pagesConfig.pageName;

  const mainFiltersState = useSelector((state: IRootState) => state.mainFilters);
  const mainFilterStateMarketPremium = useSelector((state: IRootState) => state.mainFiltersMarketPremium);
  const initialFiltersState = useSelector((state: IRootState) => state.initialFilters);

  const dispatch = useDispatch();
  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);

  const locationFilters = [FilterKeys.CITY, FilterKeys.REGION, FilterKeys.ONLINE_OFFLINE];

  const noOptions: { options: Option[]; label: string } = { options: [], label: NO_OPTIONS };

  /**
   * Retrieves the list of Filters applied onto the charts from OR/PA/TA
   * as these charts within these page have feature selector
   * @returns A list of applied filters on the charts.
   */
  const additionalFilters = (): Filters[] => {
    switch (currentPage) {
      case PageType.OPPORTUNITY_REVIEW:
      case PageType.PERFORMANCE_ANALYSIS:
      case PageType.EXPLORE:
        return getChartFilters(mainFiltersState);
      case PageType.TRADING_ASSISTANT:
        return getChartFilters(mainFilterStateMarketPremium);
    }
    return [];
  };

  const currentSelection: Filters[] = [
    ...pagesConfig.selectedFilters[currentPage].selectedFilters,
    ...additionalFilters(),
  ];

  /*
   * function that checks the state of the Brand, Priceband, Channel and Location filters.
   * Priceband+Brand, Brand+Location, Location+Priceband only, Priceband+Channel, Channel+Brand, Channel+Location.
   * Combo of all 4 is not allowed.
   */
  const checkFourFilterTypesOptions = (currentFilter: Filters): { options: Option[]; label: string } => {
    const filterKeyMap: Record<string, FilterKeys> = {
      brand: FilterKeys.BRAND,
      priceband: FilterKeys.PRICEBAND,
      split: FilterKeys.ONLINE_OFFLINE,
      region: FilterKeys.REGION,
      city: FilterKeys.CITY,
      channel: FilterKeys.CHANNEL,
    };

    const indices = Object.fromEntries(
      Object.entries(filterKeyMap).map(([key, value]) => [
        key,
        currentSelection.findIndex(({ filterKey }) => filterKey === value),
      ]),
    );

    const locationApplied = indices.split !== -1 || indices.region !== -1 || indices.city !== -1 ? 1 : -1;
    let checkIfFourFilterTypesSelected = false;

    const { filterKey: currentFilterKey } = currentFilter;

    if (currentFilterKey === FilterKeys.BRAND) {
      checkIfFourFilterTypesSelected = checkFourFilterTypesSelected(
        locationApplied,
        indices.channel,
        indices.priceband,
        MaxAllowedFilters.PerformanceAnalyzer,
      );
    }

    if (currentFilterKey === FilterKeys.PRICEBAND) {
      checkIfFourFilterTypesSelected = checkFourFilterTypesSelected(
        locationApplied,
        indices.channel,
        indices.brand,
        MaxAllowedFilters.PerformanceAnalyzer,
      );
    }

    if (currentFilterKey === FilterKeys.CHANNEL) {
      checkIfFourFilterTypesSelected = checkFourFilterTypesSelected(
        locationApplied,
        indices.brand,
        indices.priceband,
        MaxAllowedFilters.PerformanceAnalyzer,
      );
    }

    if ([FilterKeys.ONLINE_OFFLINE, FilterKeys.REGION, FilterKeys.CITY].includes(currentFilterKey)) {
      checkIfFourFilterTypesSelected = checkFourFilterTypesSelected(
        indices.brand,
        indices.channel,
        indices.priceband,
        MaxAllowedFilters.PerformanceAnalyzer,
      );
    }

    if (currentFilterKey === FilterKeys.SEGFEAT) {
      checkAddSegFeatureOptions(currentFilter);
    }

    return checkIfFourFilterTypesSelected ? noOptions : { options: currentFilter.options, label: 'All' };
  };

  /*
   * function that checks the state of the Split / Geography filters
   * if city is selected, region should have no options, and vice versa
   */
  const checkGeoOptions = (currentFilter: Filters): { options: Option[]; label: string } => {
    const indexes = {
      split: currentSelection.findIndex((e) => e.filterKey === FilterKeys.ONLINE_OFFLINE),
      region: currentSelection.findIndex((e) => e.filterKey === FilterKeys.REGION),
      city: currentSelection.findIndex((e) => e.filterKey === FilterKeys.CITY),
    };

    const filterDependencies = {
      [FilterKeys.ONLINE_OFFLINE]: [indexes.city, indexes.region],
      [FilterKeys.REGION]: [indexes.split, indexes.city],
      [FilterKeys.CITY]: [indexes.split, indexes.region],
    };

    const hasConflictingFilters = filterDependencies[currentFilter.filterKey].some((index: number) => index !== -1);

    return hasConflictingFilters ? noOptions : { options: currentFilter.options, label: 'All' };
  };

  /*
   * function that checks the state of the Product Feature filters
   * only one feature can be selected at a time
   */
  const checkProductFeatureOptions = (currentFilter: Filters): { options: Option[]; label: string } => {
    // If current filter not product features or selected filters difference with applied filters, return All Options
    if (!isCurrentFilterProductFeature(currentFilter) || isSelectedDiffWIthAppliedFilters(pagesConfig, currentPage)) {
      return { options: currentFilter.options, label: 'All' };
    }

    const featureIndex = currentSelection.findIndex((e) => e.filterKey === FilterKeys.ADDFEAT);

    // Logic if current filter is ADDFEAT
    if (currentFilter.filterKey === FilterKeys.ADDFEAT) {
      const toReturn = checkAddSegFeatureOptions(currentFilter);
      if (featureIndex !== -1) {
        const feature = currentSelection[featureIndex];
        const isSameTitle = feature.filterTitle === currentFilter.filterTitle;
        if (isSameTitle || feature.options.length !== 0) {
          return isSameTitle ? toReturn : noOptions;
        }
      }
      return toReturn;
    }

    // Logic if current filter is SEGFEAT
    return checkAddSegFeatureOptions(currentFilter);
  };

  /*
   * function that checks the options available for addfeat when segfeat is selected and vice versa
   */
  const checkAddSegFeatureOptions = (currentFilter: Filters): { options: Option[]; label: string } => {
    const { filterKey, feature, options } = currentFilter;
    const { segmentByFeature, feature: mainFeature } = mainFiltersState ?? {};

    if (
      !isSideFiltersFeatureFlagEnabled &&
      segmentByFeature &&
      segmentByFeature.feature !== feature &&
      mainFeature?.feature !== feature
    ) {
      return noOptions;
    }
    const featureConfig = {
      [FilterKeys.SEGFEAT]: pagesConfig.segFeatures,
      [FilterKeys.ADDFEAT]: pagesConfig.addFeatures,
    };

    const addSegFeatures = featureConfig[filterKey] ?? [];

    return addSegFeatures.length === 0
      ? {
          options,
          label: 'All',
        }
      : setFeaturePageConfig(addSegFeatures, currentFilter);
  };

  const setFeaturePageConfig = (
    addSegFeatures: Filters[] | undefined,
    currentFilter: Filters,
  ): { options: Option[]; label: string } => {
    if (!addSegFeatures) return { options: currentFilter.options, label: 'All' };

    if (addSegFeatures.length !== 0) {
      if (addSegFeatures[0] == null) return noOptions;

      const index = addSegFeatures.findIndex((e) => e.filterKey === FilterKeys.SEGFEAT);
      const toDisplayType = index !== -1 ? FilterKeys.SEGFEAT : FilterKeys.ADDFEAT;

      if (currentFilter.filterKey === toDisplayType) {
        const matchedIndex = addSegFeatures.findIndex((e) => e.filterTitle === currentFilter.filterTitle);

        return matchedIndex !== -1 ? { options: addSegFeatures[matchedIndex].options, label: 'All' } : noOptions;
      }
    }
    return { options: currentFilter.options, label: 'All' };
  };

  /*
   * function that returns the contents of Select component
   */
  const selectMenuOptions = (selectFilter: Filters): Option[] => {
    const mutuallyExclusiveKeys = [
      FilterKeys.BRAND,
      FilterKeys.CHANNEL,
      FilterKeys.PRICEBAND,
      FilterKeys.ONLINE_OFFLINE,
      FilterKeys.REGION,
      FilterKeys.CITY,
    ];
    const results =
      locationFilters.indexOf(selectFilter.filterKey) > -1
        ? checkGeoOptions(selectFilter).options
        : checkProductFeatureOptions(selectFilter).options;
    return results.length !== 0 &&
      mutuallyExclusiveKeys.includes(selectFilter.filterKey) &&
      !isSideFiltersFeatureFlagEnabled
      ? checkFourFilterTypesOptions(selectFilter).options
      : results;
  };

  /*
   * function that returns the display value of Select component
   */
  const selectMenuLabel = (selectFilter: Filters) => {
    const results =
      locationFilters.indexOf(selectFilter.filterKey) > -1
        ? checkGeoOptions(selectFilter).label
        : checkProductFeatureOptions(selectFilter).label;

    return results === 'All' && !isSideFiltersFeatureFlagEnabled
      ? checkFourFilterTypesOptions(selectFilter).label
      : results;
  };

  const { selectedFilter: selectedProductGroups } = useFilters(currentPage, FilterKeys.PRODUCT_GROUP);

  /**
   * Hides necessary side filters.
   *
   * CHANNEL from Retailer Overview & Category Leaderboard page
   * @param filterObj
   */
  const getFiltersWithHiddenApplied = (filterObj: SubGroupFilter | FilterObject): SubGroupFilter | FilterObject => {
    const filterFiltersByFilterKey = (filterOb: SubGroupFilter | FilterObject, filterKey: FilterKeys) => {
      return filterOb.filters.filter((sideMenuFilter) => sideMenuFilter.filterKey !== filterKey);
    };

    const selectedCountry = pagesConfig.selectedFilters[currentPage].selectedFilters.find(
      (selectedFilter) => selectedFilter.filterKey === FilterKeys.COUNTRY,
    )?.options[0].value;

    const initialFilterForSelectedCountry = initialFiltersState.initialFilters.find(
      (filter: InitialFilter) => filter.country === selectedCountry,
    );

    if (
      FilterCarryOverScope.RO_CL.getPageTypes().includes(currentPage) &&
      (!selectedCountry ||
        !initialFilterForSelectedCountry ||
        (!initialFilterForSelectedCountry.isChannelFilterAllowed && selectedProductGroups.length !== 1))
    ) {
      filterObj.filters = filterFiltersByFilterKey(filterObj, FilterKeys.CHANNEL);
    }

    if (PageName.TRADING_ASSISTANT === currentPageName) {
      filterObj.filters = filterFiltersByFilterKey(filterObj, FilterKeys.BRAND);
    }
    return filterObj;
  };

  const isMultiProductGroups = selectedProductGroups.length > 1 || selectedProductGroups.length === 0;

  /**
   * Determines whether to disable the pricebands dropdown.
   *
   * It is disabled only if:
   * 1. The selected country's default currency is not selected.
   * 2. No priceband options available.
   *
   * @param options - Priceband options.
   */
  const isDisablePricebands = (options: Option[]): boolean => {
    return isEmptyOrNoOptions(options) || isCountryMultiSelect() || isMultiProductGroups;
  };

  const isDisableMultiCountry = (options: Option[]) => {
    const selectedFilters = pagesConfig.selectedFilters[currentPage].selectedFilters;
    const selectedCountry = selectedFilters.find(byFilterKey(FilterKeys.COUNTRY));

    if (!selectedCountry) {
      return isEmptyOrNoOptions(options);
    }

    return selectedCountry.options.length > 1;
  };

  /*
   * function that returns the Select component of the filters to be rendered
   */
  const selectComponent = (filter: SubGroupFilter | FilterObject) => {
    if (!filter?.filters?.[0]) {
      return;
    }

    filter = getFiltersWithHiddenApplied(filter);

    const onChangeFunc = (toUpdate: Filters) => {
      updateSelectedFilters(pagesConfig, pagesConfig.pageType, toUpdate);
    };
    return (
      <div data-testid={`filter-items-${filterObject.filterGroup}`}>
        {filter.filters.map((selectFilter: Filters) => {
          let options: Option[] = selectMenuOptions(selectFilter);
          const value: Option[] = getDisplayValue(
            pagesConfig,
            updateSideFilters,
            pagesConfig.pageType,
            selectFilter,
            options,
          );

          const { filterKey } = selectFilter;

          if (filterKey === FilterKeys.PRICEBAND) {
            const currencyExchangeRate = getExchangeRate(pagesConfig);

            options = options.map(({ value }) => {
              return {
                label: formatPriceBandAsPerExchangeRate(value, currencyExchangeRate),
                value,
              };
            });
          }

          const isMultiEnabeld =
            [PageType.EXPLORE, PageType.OPPORTUNITY_REVIEW, PageType.TRADING_ASSISTANT].includes(currentPage) &&
            filterKey === FilterKeys.PRODUCT_GROUP
              ? !isDisableMultiCountry(options)
              : isMulti(filterKey, currentPage, pagesConfig);

          const filterSelectorKey = `filter-item-${filterKey}-${selectFilter.filterTitle}-${options
            .map((option) => option.value)
            .join('')}`;

          const exclusion: ExclusionProps = {
            isAvailable: canShowExclusionToggle(filterKey, currentPage),
            isActive: isExclusionActiveForFilter(filterKey, currentPage, pagesConfig),
          };

          return (
            <FilterSelector
              key={filterSelectorKey}
              selectFilter={selectFilter}
              placeholder={selectMenuLabel(selectFilter)}
              options={options}
              value={value}
              isMulti={isMultiEnabeld}
              isVirtualList={isVirtualList && selectFilter.filterKey === FilterKeys.BRAND}
              disableSelectorPredicate={
                [PageName.EXPLORE, PageName.PERFORMANCE_ANALYSIS, PageName.OPPORTUNITY_REVIEW, PageName.TRADING_ASSISTANT].includes(
                  currentPageName,
                ) && filterKey === FilterKeys.PRICEBAND
                  ? isDisablePricebands
                  : undefined
              }
              isDisabledMultiSelect={isCountryMultiSelect()}
              onChangeFunc={onChangeFunc}
              exclusion={exclusion}
            />
          );
        })}
      </div>
    );
  };

  /*
   * function that returns true if the current selected options for country side filter > 1
   */
  const isCountryMultiSelect = () => {
    const countryIndex = currentSelection.findIndex((e) => e.filterKey === FilterKeys.COUNTRY);
    return countryIndex !== -1 ? currentSelection[countryIndex].options.length > 1 : false;
  };

  /*
   * function that renders the filters for those that are under an Accordion
   */
  const toRenderWithAccordion = () => {
    const { Accordion } = containers;
    return (
      <>
        {filterObject.subGroupFilters.map((sub) => (
          <Accordion
            flushAlign={true}
            key={sub.subGroupTitle}
            hoverColor="bg-medium"
            iconRight={true}
            disabled={isCommonFeatureFilterAppliedInExplorePage(filterObject)}
          >
            <Accordion.Item key={sub.subGroupTitle} title={sub.subGroupTitle} data-testid="filter-accordion">
              {selectComponent(sub)}
            </Accordion.Item>
          </Accordion>
        ))}
      </>
    );
  };

  const isCommonFeatureFilterAppliedInExplorePage = (filterObject: FilterObject): boolean => {
    if (isMultiPgCommonFeatureAllowed(pagesConfig.pageType)) {
      return (
        selectedProductGroups.length === 0 ||
        filterObject.subGroupFilters.every(function (filter) {
          return filter.filters.length === 0;
        })
      );
    }
    return (
      isMultiProductGroups ||
      filterObject.subGroupFilters.every(function (filter) {
        return filter.filters.length === 0;
      })
    );
  };

  /*
   * function that renders the filters for those that may have an accordion
   */
  const toRenderSubGroup = () => {
    // checks if all the filters under subGroupFilters are empty or not
    const allSubGroupFiltersEmpty = isCommonFeatureFilterAppliedInExplorePage(filterObject);

    // if it has both normal and subgroup filters, and there are valid filters under the subgroup
    if (filterObject.hasNormalAndSubGroup && !allSubGroupFiltersEmpty) {
      return (
        <div>
          <div>{selectComponent(filterObject)}</div>
          <div>{toRenderWithAccordion()}</div>
        </div>
      );
    }

    /*
     * if it has both normal and subgroup filters, and there are no valid filters under the subgroup
     * eg. PG that has no Product Features at all
     */
    if (filterObject.hasNormalAndSubGroup && allSubGroupFiltersEmpty) {
      return (
        <div>
          <div>{selectComponent(filterObject)}</div>
        </div>
      );
    }

    // if it does not have subgroup filters
    return (
      <div>
        <div>{toRenderWithAccordion()}</div>
      </div>
    );
  };
  return (
    <div>
      {!pagesConfig.isSideFiltersFeatureFlagEnabled && <div>{filterObject.mainTitle}</div>}
      <div>{filterObject.hasSubGroup ? toRenderSubGroup() : selectComponent(filterObject)}</div>
    </div>
  );
};

export default FilterComponent;
